import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { groupSpecialSlots } from '../utils/groupSpecialSlots'
import { IdoneityUploadDialog } from './IdoneityUploadDialog'
import { DailyDocumentUploadDialog } from './DailyDocumentUploadDialog'
import type { ExtraData, SlotGroup } from '../utils/groupSpecialSlots'
import type {
  Appointment,
  AppointmentStatus,
  CalculatedSlot,
  DoctorSlotsResponse,
  Document,
  SlotState,
} from '@shared/schemas'
import type { BookingActionResponse } from '../queries'
import { cn, useNotification, useTranslation } from '@/core'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconRenderer,
} from '@/ui'

// ============================================
// TYPES
// ============================================

interface DailyAgendaViewProps {
  /** Selected date */
  date: string
  /** Doctor slots data (single doctor mode) */
  doctorSlots?: DoctorSlotsResponse
  /** Multi-doctor slots data */
  multiDoctorSlots?: Array<DoctorSlotsResponse>
  /** All appointments for the day (used when not in slot-based mode) */
  appointments?: Array<Appointment>
  /** View mode: single doctor or multiple doctors */
  viewMode: 'single' | 'multi'
  /** User role for permissions */
  userRole?: string
  /** Loading state */
  isLoading?: boolean
  /** Doctor availability status map (doctorId -> isAvailable) */
  doctorsAvailability?: Record<string, boolean>
  /** Daily note to display as banner */
  note?: { content: string; author?: string } | null
  /** Callback when clicking the note banner */
  onNoteClick?: () => void
  /** Callback when clicking add on a slot */
  onAddBooking: (
    date: string,
    time: string,
    doctorId?: string,
    extraCompanyId?: string,
  ) => void
  /** Callback when clicking an appointment */
  onAppointmentClick: (appointment: Appointment, doctorId?: string) => void
  /** Callback when deleting an appointment */
  onDelete: (id: string) => void
  /** Callback when navigating to previous day */
  onPreviousDay: () => void
  /** Callback when navigating to next day */
  onNextDay: () => void
  /** Callback when clicking add on analysis-only column */
  onAddAnalysisOnlyBooking?: (date: string, time: string) => void
  /** Callback when duplicating an appointment */
  onDuplicate: (appointment: Appointment) => void
  /** Callback when moving an appointment to a new slot */
  onMoveAppointment?: (
    appointment: Appointment,
    newDate: string,
    newTime: string,
    newDoctorId?: string,
  ) => Promise<unknown>
  dailyDocuments: Array<Document>
  companyIdsWithUsers: ReadonlySet<string>
  updateAppointmentStatus: (
    id: string,
    status: AppointmentStatus,
  ) => Promise<unknown>
  updateAppointmentAcceptance: (
    id: string,
    acceptance: boolean,
  ) => Promise<unknown>
  removeAppointmentIdoneity: (id: string) => Promise<unknown>
  isRemovingAppointmentIdoneity: boolean
  uploadIdoneity: (
    appointmentId: string,
    file: File,
    notes: string,
  ) => Promise<unknown>
  isUploadingIdoneity: boolean
  markAllAppointmentsCompleted: (date: string) => Promise<unknown>
  isMarkingAllAppointmentsCompleted: boolean
  sendPatientReminder: (
    id: string,
  ) => Promise<BookingActionResponse | undefined>
  sendCompanyReminder: (
    id: string,
    scope: 'single' | 'all_company_for_date',
  ) => Promise<BookingActionResponse | undefined>
  sendCompanyConfirmation: (
    id: string,
    scope: 'single' | 'all_company_for_date',
  ) => Promise<BookingActionResponse | undefined>
  sendPatientDataReminder: (
    id: string,
  ) => Promise<BookingActionResponse | undefined>
  sendCompanyConfirmationRequest: (
    id: string,
    scope: 'single' | 'all_company_for_date',
  ) => Promise<BookingActionResponse | undefined>
  sendNoShowNotification: (
    id: string,
  ) => Promise<BookingActionResponse | undefined>
  sendDoctorAbsenceNotification: (input: {
    doctorId: string
    date: string
    appointmentId?: string
  }) => Promise<BookingActionResponse | undefined>
  isSendingPatientReminder: boolean
  isSendingCompanyReminder: boolean
  isSendingCompanyConfirmation: boolean
  isSendingPatientDataReminder: boolean
  isSendingCompanyConfirmationRequest: boolean
  isSendingNoShowNotification: boolean
  isSendingDoctorAbsenceNotification: boolean
  uploadDailyDocuments: (
    agendaDate: string,
    files: Array<File>,
  ) => Promise<unknown>
  isUploadingDailyDocuments: boolean
}

interface AppointmentCardActions {
  companyIdsWithUsers: ReadonlySet<string>
  updateStatus: (id: string, status: AppointmentStatus) => Promise<unknown>
  updateAcceptance: (id: string, acceptance: boolean) => Promise<unknown>
  removeIdoneity: (id: string) => Promise<unknown>
  isRemovingIdoneity: boolean
  uploadIdoneity: (
    appointmentId: string,
    file: File,
    notes: string,
  ) => Promise<unknown>
  isUploadingIdoneity: boolean
}

const AppointmentCardActionsContext =
  createContext<AppointmentCardActions | null>(null)

function useAppointmentCardActions() {
  const actions = useContext(AppointmentCardActionsContext)
  if (!actions) {
    throw new Error('AppointmentCard must be rendered inside DailyAgendaView')
  }
  return actions
}

// ============================================
// CONSTANTS
// ============================================

const ANALYSIS_START_TIME = '08:00'

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('it-IT', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const getSlotStateStyles = (state: SlotState): string => {
  switch (state) {
    case 'available':
      return 'bg-green-50 hover:bg-green-100 border-green-200'
    case 'blocked':
      return 'bg-red-50 border-red-200'
    case 'booked':
      return 'bg-blue-50 border-blue-200'
    default:
      return 'bg-gray-50 border-gray-200'
  }
}

const getVisitTypeColor = (visitType: string): string => {
  return visitType === 'work' ? 'border-l-blue-500' : 'border-l-emerald-500'
}

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * Single appointment card within a slot
 */
const AppointmentCard = memo(function AppointmentCard({
  appointment,
  userRole,
  onClick,
  onDelete,
  onDuplicate,
  onContextMenu,
  isBeingMoved,
  isMoveModeActive,
}: {
  appointment: Appointment
  userRole?: string
  onClick: () => void
  onDelete: (id: string) => void
  onDuplicate: (appointment: Appointment) => void
  onContextMenu?: (e: React.MouseEvent, appointment: Appointment) => void
  isBeingMoved?: boolean
  isMoveModeActive?: boolean
}) {
  const { t } = useTranslation()
  const actions = useAppointmentCardActions()
  const [isIdoneityDialogOpen, setIsIdoneityDialogOpen] = useState(false)
  const canUpdateStatus = useMemo(
    () => userRole === 'doctor' || userRole === 'admin',
    [userRole],
  )
  const isCompanyWithUser = actions.companyIdsWithUsers.has(
    appointment.companyId,
  )
  const canDelete = useMemo(() => userRole === 'admin', [userRole])

  const handleStatusChange = useCallback(
    (e: React.MouseEvent, status: AppointmentStatus) => {
      e.stopPropagation()
      if (appointment.id) {
        void actions.updateStatus(appointment.id, status)
      }
    },
    [actions, appointment.id],
  )

  const handleAcceptanceToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (appointment.id) {
        void actions.updateAcceptance(appointment.id, !appointment.acceptance)
      }
    },
    [actions, appointment.acceptance, appointment.id],
  )

  // Eligibility Update Logic
  const canUpdateEligibility = useMemo(
    () => userRole === 'admin' && isCompanyWithUser,
    [userRole, isCompanyWithUser],
  )

  // Eligibility is refreshed through the mutation invalidation configured by the page.
  // For now let's rely on the appointment prop being updated via parent invalidation
  const currentEligibility = isCompanyWithUser
    ? appointment.patientEligibility
    : null

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (appointment.id) {
        onDelete(appointment.id)
      }
    },
    [appointment.id, onDelete],
  )

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onDuplicate(appointment)
    },
    [appointment, onDuplicate],
  )

  const statusConfig = useMemo(
    () => ({
      prenotata: {
        label: t('booking.statusBooked') || 'Prenotata',
        icon: 'calendar-check',
        bgClass: 'bg-blue-100',
        textClass: 'text-blue-800',
        borderClass: 'border-blue-200',
      },
      in_attesa: {
        label: t('booking.statusPending') || 'In Attesa',
        icon: 'clock',
        bgClass: 'bg-yellow-100',
        textClass: 'text-yellow-800',
        borderClass: 'border-yellow-200',
      },
      in_corso: {
        label: t('booking.statusInProgress') || 'In Corso',
        icon: 'play-circle',
        bgClass: 'bg-orange-100',
        textClass: 'text-orange-800',
        borderClass: 'border-orange-200',
      },
      effettuata: {
        label: t('booking.statusCompleted') || 'Effettuata',
        icon: 'check-circle',
        bgClass: 'bg-green-100',
        textClass: 'text-green-800',
        borderClass: 'border-green-200',
      },
      non_presentato: {
        label: t('booking.statusNoShow') || 'Non Presentato',
        icon: 'user-x',
        bgClass: 'bg-gray-100',
        textClass: 'text-gray-800',
        borderClass: 'border-gray-300',
      },
    }),
    [t],
  )

  const currentStatus = useMemo(() => appointment.status, [appointment.status])
  const config = useMemo(
    () => statusConfig[currentStatus],
    [currentStatus, statusConfig],
  )

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (onContextMenu) {
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(e, appointment)
      }
    },
    [onContextMenu, appointment],
  )

  return (
    <div
      onClick={isMoveModeActive && !isBeingMoved ? undefined : onClick}
      onContextMenu={handleContextMenu}
      className={cn(
        'flex flex-col gap-2 p-3 rounded-md border border-l-4 relative group',
        isMoveModeActive && !isBeingMoved
          ? 'bg-white/60 pointer-events-none'
          : 'bg-white hover:bg-gray-50 cursor-pointer',
        'transition-colors',
        getVisitTypeColor(appointment.visitType),
        isBeingMoved &&
          'ring-2 ring-blue-500 ring-offset-1 animate-pulse pointer-events-auto',
      )}
    >
      {/* Row 1: Patient info + Kebab */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm leading-snug truncate">
            {`${appointment.patientName}${appointment.patientJobRoleName ? ` (${appointment.patientJobRoleName})` : ''}`}
          </div>
          {(appointment.patientDateOfBirth ||
            appointment.patientPlaceOfBirth) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
              {appointment.patientDateOfBirth && (
                <span>
                  {new Date(appointment.patientDateOfBirth).toLocaleDateString(
                    'it-IT',
                  )}
                </span>
              )}
              {appointment.patientDateOfBirth &&
                appointment.patientPlaceOfBirth && (
                  <span className="opacity-40">·</span>
                )}
              {appointment.patientPlaceOfBirth && (
                <span>{appointment.patientPlaceOfBirth}</span>
              )}
            </div>
          )}
          <div className="text-xs font-medium text-muted-foreground truncate mt-0.5">
            {appointment.companyName}
          </div>
        </div>

        {/* Kebab: azioni secondarie raccolte, visibile solo on-hover */}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={(props) => (
              <button
                {...props}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  props.onClick?.(e)
                }}
                className={cn(
                  'appearance-none bg-transparent shrink-0',
                  'inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100',
                  'opacity-0 group-hover:opacity-100 transition-opacity',
                  props.className,
                )}
                title="Azioni"
              >
                <IconRenderer name="more-horizontal" className="w-4 h-4" />
              </button>
            )}
          />
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleDuplicate}
              className="flex items-center gap-2"
            >
              <IconRenderer name="copy" className="w-4 h-4" />
              <span>{t('common.duplicate') || 'Duplica'}</span>
            </DropdownMenuItem>
            {canDelete && (
              <DropdownMenuItem
                onClick={handleDelete}
                className="flex items-center gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
              >
                <IconRenderer name="trash-2" className="w-4 h-4" />
                <span>{t('common.delete')}</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row 2: Status + Accettazione (icon-only) + Dottore */}
      <div className="flex items-center flex-wrap gap-1.5">
        {canUpdateStatus ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={(props) => (
                <button
                  {...props}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    props.onClick?.(e)
                  }}
                  className={cn(
                    'appearance-none bg-transparent',
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer',
                    config.bgClass,
                    config.textClass,
                    config.borderClass,
                    'hover:opacity-80',
                    props.className,
                  )}
                  title={t('booking.changeStatus')}
                >
                  <IconRenderer name={config.icon} className="w-3 h-3 mr-1" />
                  {config.label}
                  <IconRenderer name="chevron-down" className="w-3 h-3 ml-1" />
                </button>
              )}
            />
            <DropdownMenuContent align="end">
              {(Object.keys(statusConfig) as Array<AppointmentStatus>).map(
                (status) => (
                  <DropdownMenuItem
                    key={status}
                    onClick={(e) => handleStatusChange(e, status)}
                    disabled={status === currentStatus}
                    className={cn(
                      'flex items-center gap-2',
                      status === currentStatus && 'bg-gray-100',
                    )}
                  >
                    <IconRenderer
                      name={statusConfig[status].icon}
                      className={cn('w-4 h-4', statusConfig[status].textClass)}
                    />
                    <span>{statusConfig[status].label}</span>
                    {status === currentStatus && (
                      <IconRenderer name="check" className="w-4 h-4 ml-auto" />
                    )}
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span
            className={cn(
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
              config.bgClass,
              config.textClass,
              config.borderClass,
            )}
          >
            <IconRenderer name={config.icon} className="w-3 h-3 mr-1" />
            {config.label}
          </span>
        )}

        {/* Accettazione — icon-only con tooltip, risparmia ~110px */}
        {canUpdateStatus ? (
          <button
            type="button"
            onClick={handleAcceptanceToggle}
            className={cn(
              'appearance-none bg-transparent',
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer hover:opacity-80',
              appointment.acceptance
                ? 'bg-green-100 text-green-700 border-green-200'
                : 'bg-red-100 text-red-700 border-red-200',
            )}
            title="Cambia accettazione"
          >
            <IconRenderer
              name={appointment.acceptance ? 'check' : 'x'}
              className="w-3 h-3"
            />
            Accettazione
          </button>
        ) : (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
              appointment.acceptance
                ? 'bg-green-100 text-green-700 border-green-200'
                : 'bg-red-100 text-red-700 border-red-200',
            )}
          >
            <IconRenderer
              name={appointment.acceptance ? 'check' : 'x'}
              className="w-3 h-3"
            />
            Accettazione
          </span>
        )}

        <span className="text-xs text-muted-foreground">
          {appointment.analysisOnly
            ? t('booking.analysisOnlyLabel')
            : appointment.doctorName}
        </span>
      </div>

      {/* Esami */}
      {appointment.exams && appointment.exams.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {appointment.exams.map((exam) => (
            <span
              key={exam.id}
              className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-sm italic"
            >
              {exam.name}
            </span>
          ))}
        </div>
      )}

      {/* Note appuntamento */}
      {appointment.notes && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200/50">
          <IconRenderer
            name="message-square"
            className="flex-none text-amber-600 mt-0.5"
            size={'sm'}
          />
          <div className="leading-relaxed opacity-90 break-words whitespace-pre-wrap min-w-0">
            {appointment.notes}
          </div>
        </div>
      )}

      {/* Eligibility Indicator / Control */}
      {canUpdateEligibility ? (
        <div
          className="mt-2 flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {currentEligibility === 'idoneo' ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={(props) => (
                  <button
                    {...props}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation() // Prevent card click
                      props.onClick?.(e)
                    }}
                    className={cn(
                      'appearance-none bg-transparent',
                      'text-xs px-1.5 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 flex items-center gap-1 cursor-pointer hover:bg-green-100 transition-colors',
                      props.className,
                    )}
                  >
                    <IconRenderer name="check-circle" className="w-3 h-3" />
                    {t('booking.idoneitySent')}
                    <IconRenderer
                      name="chevron-down"
                      className="w-3 h-3 ml-1"
                    />
                  </button>
                )}
              />
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsIdoneityDialogOpen(true)
                  }}
                >
                  <IconRenderer name="edit-2" className="w-4 h-4 mr-2" />
                  {t('common.modify')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (appointment.id && confirm(t('common.confirmDelete'))) {
                      void actions.removeIdoneity(appointment.id)
                    }
                  }}
                  disabled={actions.isRemovingIdoneity}
                >
                  <IconRenderer name="trash-2" className="w-4 h-4 mr-2" />
                  {t('common.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={(e) => {
                e.stopPropagation()
                setIsIdoneityDialogOpen(true)
              }}
            >
              <IconRenderer name="file-up" className="w-3 h-3 mr-1" />
              {t('booking.sendIdoneity')}
            </Button>
          )}
        </div>
      ) : currentEligibility ? (
        <div className="mt-1">
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded font-medium border',
              currentEligibility === 'idoneo'
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200',
            )}
          >
            {currentEligibility === 'idoneo'
              ? t('patient.eligible')
              : t('patient.notEligible')}
          </span>
        </div>
      ) : null}

      {/* Idoneity Upload Dialog */}
      <IdoneityUploadDialog
        open={isIdoneityDialogOpen}
        onOpenChange={setIsIdoneityDialogOpen}
        patientName={appointment.patientName}
        companyName={appointment.companyName}
        onSubmit={async ({ file, notes }) => {
          await actions.uploadIdoneity(appointment.id || '', file, notes)
        }}
        isPending={actions.isUploadingIdoneity}
      />
    </div>
  )
})

/**
 * Slot row for single doctor view
 */
const SlotRow = memo(function SlotRow({
  time,
  displayTime,
  slot,
  appointments,
  userRole,
  onAddBooking,
  onAppointmentClick,
  onDelete,
  onDuplicate,
  onContextMenu,
  movingAppointmentId,
  onSlotClickForMove,
}: {
  time: string
  displayTime?: string
  slot?: CalculatedSlot
  appointments: Array<Appointment>
  userRole?: string
  onAddBooking: () => void
  onAppointmentClick: (appointment: Appointment, doctorId?: string) => void
  onDelete: (id: string) => void
  onDuplicate: (appointment: Appointment) => void
  onContextMenu?: (e: React.MouseEvent, appointment: Appointment) => void
  movingAppointmentId?: string
  onSlotClickForMove?: (time: string) => void
}) {
  const { t } = useTranslation()
  const state = slot?.state || 'blocked'
  const canAdd =
    (state === 'available' || state === 'booked') && userRole === 'admin'
  const isHourStart = time.endsWith(':00')
  const slotStyle = getSlotStateStyles(state)

  return (
    <div
      className={cn(
        'flex border-b min-h-[60px]',
        isHourStart && 'border-t-2 border-t-gray-300',
      )}
    >
      {/* Time column */}
      <div
        className={cn(
          'w-20 shrink-0 flex items-center justify-center text-sm font-medium',
          'border-r bg-gray-50 sticky left-0 shadow-[1px_0_3px_rgba(0,0,0,0.05)]',
          isHourStart ? 'text-gray-900' : 'text-gray-400',
        )}
      >
        {displayTime ?? time}
      </div>

      {/* Content column */}
      <div
        className={cn(
          'flex-1 p-2 flex items-start gap-2 min-h-[60px]',
          slotStyle,
          movingAppointmentId &&
            state !== 'blocked' &&
            'ring-2 ring-inset ring-blue-300 cursor-move',
        )}
        onClick={() => {
          if (movingAppointmentId && state !== 'blocked') {
            onSlotClickForMove?.(time)
            return
          }
          if (state === 'available') onAddBooking()
        }}
      >
        {/* Extra location and/or company indicator */}
        {(slot?.extraLocation || slot?.extraCompanyName) && (
          <div className="flex-1">
            <div className="flex flex-col gap-1 p-2 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center gap-2">
                {slot.extraCompanyName ? (
                  <>
                    <IconRenderer
                      name="building"
                      className="w-4 h-4 text-green-600 shrink-0"
                    />
                    <span className="font-medium text-sm text-green-700">
                      {slot.extraCompanyName}
                    </span>
                  </>
                ) : (
                  <>
                    <IconRenderer
                      name="map-pin"
                      className="w-4 h-4 text-green-600 shrink-0"
                    />
                    <span className="font-medium text-sm text-green-700">
                      {slot.extraLocation}
                    </span>
                  </>
                )}
                <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-green-100 text-green-600 rounded font-medium">
                  {t('availability.override.extra')}
                </span>
              </div>
              {/* Show address if company is selected and has different address than extraLocation */}
              {slot.extraCompanyName &&
                (slot.extraCompanyAddress || slot.extraLocation) && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 pl-6">
                    <IconRenderer
                      name="map-pin"
                      className="w-3 h-3 text-green-500 shrink-0"
                    />
                    <span>
                      {slot.extraCompanyAddress || slot.extraLocation}
                    </span>
                  </div>
                )}
            </div>
          </div>
        )}
        {/* Site Inspection indicator */}
        {slot?.siteInspection && (
          <div className="flex-1">
            <div className="flex flex-col gap-1 p-2 bg-orange-50 border border-orange-200 rounded-md">
              <div className="flex items-center gap-2">
                <IconRenderer
                  name="clipboard-check"
                  className="w-4 h-4 text-orange-600 shrink-0"
                />
                <span className="font-medium text-sm text-orange-700">
                  {slot.siteInspection.companyName ||
                    t('booking.siteInspection')}
                </span>
                <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded font-medium">
                  {t('booking.siteInspection')}
                </span>
              </div>
              {slot.siteInspection.address && (
                <div className="flex items-center gap-2 text-xs text-gray-600 pl-6">
                  <IconRenderer
                    name="map-pin"
                    className="w-3 h-3 text-orange-500 shrink-0"
                  />
                  <span>{slot.siteInspection.address}</span>
                </div>
              )}
              {slot.siteInspection.phone && (
                <div className="flex items-center gap-2 text-xs text-gray-600 pl-6">
                  <IconRenderer
                    name="phone"
                    className="w-3 h-3 text-orange-500 shrink-0"
                  />
                  <span>{slot.siteInspection.phone}</span>
                </div>
              )}
              {slot.siteInspection.notes && (
                <div className="flex items-center gap-2 text-xs text-gray-600 pl-6">
                  <IconRenderer
                    name="message-square"
                    className="w-3 h-3 text-orange-500 shrink-0"
                  />
                  <span>{slot.siteInspection.notes}</span>
                </div>
              )}
            </div>
          </div>
        )}
        {slot?.companyVisit && (
          <div className="flex-1 flex flex-col gap-1 p-2 bg-purple-50 border border-purple-200 rounded-md">
            <div className="flex items-center gap-2">
              <IconRenderer
                name="building"
                className="w-4 h-4 text-purple-600 shrink-0"
              />
              <span className="font-medium text-sm text-purple-700">
                {slot.companyVisit.companyName}
              </span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded font-medium">
                {t('availability.blockReason.companyVisit')}
              </span>
            </div>
            {slot.companyVisit.companyAddress && (
              <div className="flex items-center gap-2 text-xs text-gray-600 pl-6">
                <IconRenderer
                  name="map-pin"
                  className="w-3 h-3 text-purple-500 shrink-0"
                />
                <span>{slot.companyVisit.companyAddress}</span>
              </div>
            )}
            {slot.companyVisit.patientNames &&
              slot.companyVisit.patientNames.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-gray-600 pl-6">
                  <IconRenderer
                    name="users"
                    className="w-3 h-3 text-purple-500 shrink-0 mt-0.5"
                  />
                  <span className="flex flex-wrap gap-1">
                    {slot.companyVisit.patientNames.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-1.5 py-0.5 bg-white border border-purple-100 rounded text-[10px]"
                      >
                        {name}
                      </span>
                    ))}
                  </span>
                </div>
              )}
          </div>
        )}
        {slot?.region && (
          <div className="flex-1 flex flex-col gap-1 p-2 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center gap-2">
              <IconRenderer
                name="map"
                className="w-4 h-4 text-red-600 shrink-0"
              />
              <span className="font-medium text-sm text-red-700">
                {slot.region.reason || 'Regione'}
              </span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">
                Regione
              </span>
            </div>
          </div>
        )}

        {state === 'blocked' && (
          <div className="flex-1">
            <div className="flex items-center gap-1 text-xs text-red-600">
              <IconRenderer name="lock" className="w-4 h-4" />
              <span>{slot?.blockReason || t('agenda.blocked')}</span>
            </div>
          </div>
        )}

        {/* Appointments in this slot */}
        {appointments.length > 0 && (
          <div className="flex-1 flex flex-col gap-2">
            {appointments.map((apt) => (
              <AppointmentCard
                key={apt.id}
                appointment={apt}
                userRole={userRole}
                onClick={() => {
                  onAppointmentClick(apt, apt.doctorId ?? undefined)
                }}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onContextMenu={onContextMenu}
                isBeingMoved={movingAppointmentId === apt.id}
                isMoveModeActive={!!movingAppointmentId}
              />
            ))}
          </div>
        )}

        {/* Add button for available slots */}
        {canAdd && appointments.length === 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-green-600 hover:text-green-700 hover:bg-green-100"
            onClick={onAddBooking}
          >
            <IconRenderer name="plus" className="w-4 h-4 mr-1" />
            {t('agenda.addBooking')}
          </Button>
        )}

        {/* Add button even when there are appointments (for force booking) */}
        {canAdd && appointments.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
            onClick={onAddBooking}
          >
            <IconRenderer name="plus" className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  )
})

const timeToMinute = (time: string) => {
  const [hour, minute] = time.split(':').map(Number)
  return hour * 60 + minute
}

interface MultiDoctorAgendaProps {
  doctors: Array<DoctorSlotsResponse>
  appointments: Array<Appointment>
  userRole?: string
  movingAppointmentId?: string
  onAddBooking: (
    time: string,
    doctorId: string,
    extraCompanyId?: string,
  ) => void
  onMoveToSlot: (time: string, doctorId: string) => void
  onAppointmentClick: (appointment: Appointment, doctorId: string) => void
  onDelete: (id: string) => void
  onDuplicate: (appointment: Appointment) => void
  onContextMenu: (
    event: React.MouseEvent,
    appointment: Appointment,
    doctorId: string,
  ) => void
}

const MultiDoctorAgenda = memo(function MultiDoctorAgenda({
  doctors,
  appointments,
  userRole,
  movingAppointmentId,
  onAddBooking,
  onMoveToSlot,
  onAppointmentClick,
  onDelete,
  onDuplicate,
  onContextMenu,
}: MultiDoctorAgendaProps) {
  const slotTimes = Array.from(
    new Set(doctors.flatMap((doctor) => doctor.slots.map((slot) => slot.time))),
  ).sort((first, second) => timeToMinute(first) - timeToMinute(second))

  if (slotTimes.length === 0) return null

  return (
    <div className="flex flex-col border-b">
      {slotTimes.map((time) => (
        <div key={time} className="flex border-b last:border-b-0">
          <div className="sticky left-0 z-20 flex w-16 shrink-0 items-start justify-center border-r bg-white px-1 py-2 text-center text-xs font-semibold text-gray-600 shadow-[1px_0_3px_rgba(0,0,0,0.05)]">
            {time}
          </div>
          {doctors.map((doctor) => {
            const slot = doctor.slots.find((item) => item.time === time)
            if (!slot) {
              return (
                <div
                  key={doctor.doctorId}
                  aria-label={`${doctor.doctorName}, ${time}, bloccato`}
                  className="min-h-16 min-w-[30vw] flex-1 border-r bg-red-50/70"
                />
              )
            }

            const state = slot.state
            const slotAppointments = appointments.filter(
              (appointment) =>
                appointment.doctorId === doctor.doctorId &&
                appointment.time === slot.time,
            )
            const validMoveTarget = !!movingAppointmentId && state !== 'blocked'
            const canBook =
              state === 'available' ||
              state === 'booked' ||
              !!slot.companyVisit ||
              !!slot.region

            return (
              <div
                key={doctor.doctorId}
                role="button"
                tabIndex={canBook ? 0 : -1}
                aria-label={`${doctor.doctorName}, ${slot.time}–${slot.endTime}, ${state}`}
                className={cn(
                  'group relative min-h-16 min-w-[30vw] flex-1 border-r p-2 transition-colors focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500',
                  state === 'available' &&
                    'cursor-pointer bg-green-50/70 hover:bg-green-100',
                  state === 'booked' && 'bg-blue-50/60',
                  state === 'blocked' && 'bg-red-50/70',
                  (slot.companyVisit || slot.region) &&
                    'cursor-pointer bg-purple-50/70',
                  validMoveTarget &&
                    'z-10 cursor-move ring-2 ring-inset ring-blue-300',
                )}
                onClick={() => {
                  if (validMoveTarget) {
                    onMoveToSlot(slot.time, doctor.doctorId)
                  } else if (canBook && slotAppointments.length === 0) {
                    onAddBooking(
                      slot.time,
                      doctor.doctorId,
                      slot.extraCompanyId,
                    )
                  }
                }}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && canBook) {
                    event.preventDefault()
                    if (validMoveTarget)
                      onMoveToSlot(slot.time, doctor.doctorId)
                    else
                      onAddBooking(
                        slot.time,
                        doctor.doctorId,
                        slot.extraCompanyId,
                      )
                  }
                }}
              >
                <div className="pr-9 text-[10px] font-semibold text-gray-600">
                  {slot.time}–{slot.endTime}
                  {slot.blockReason && state === 'blocked' && (
                    <span className="ml-1 text-red-700">
                      {slot.blockReason}
                    </span>
                  )}
                  {slot.siteInspection && (
                    <span className="ml-1 text-orange-700">
                      Sopralluogo · {slot.siteInspection.companyName}
                    </span>
                  )}
                  {slot.companyVisit && (
                    <span className="ml-1 text-purple-700">
                      {slot.companyVisit.companyName}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-col gap-2">
                  {slotAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      userRole={userRole}
                      onClick={() =>
                        onAppointmentClick(appointment, doctor.doctorId)
                      }
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      onContextMenu={(event, value) =>
                        onContextMenu(event, value, doctor.doctorId)
                      }
                      isBeingMoved={movingAppointmentId === appointment.id}
                      isMoveModeActive={!!movingAppointmentId}
                    />
                  ))}
                </div>
                {userRole === 'admin' && canBook && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 size-8 rounded-full bg-white/90 p-0 opacity-0 shadow-sm group-hover:opacity-100 group-focus-within:opacity-100"
                    onClick={(event) => {
                      event.stopPropagation()
                      onAddBooking(
                        slot.time,
                        doctor.doctorId,
                        slot.extraCompanyId,
                      )
                    }}
                  >
                    <IconRenderer name="plus" className="size-4" />
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
})

// ============================================
// MAIN COMPONENT
// ============================================

export const DailyAgendaView = memo(function DailyAgendaView({
  date,
  doctorSlots,
  multiDoctorSlots,
  appointments = [],
  viewMode,
  userRole,
  doctorsAvailability = {},
  note,
  onNoteClick,
  onAddBooking,
  onAppointmentClick,
  onDelete,
  onPreviousDay,
  onNextDay,
  onAddAnalysisOnlyBooking,
  onDuplicate,
  onMoveAppointment,
  dailyDocuments,
  companyIdsWithUsers,
  updateAppointmentStatus,
  updateAppointmentAcceptance,
  removeAppointmentIdoneity,
  isRemovingAppointmentIdoneity,
  uploadIdoneity,
  isUploadingIdoneity,
  markAllAppointmentsCompleted,
  isMarkingAllAppointmentsCompleted: isMarkingAll,
  sendPatientReminder,
  sendCompanyReminder,
  sendCompanyConfirmation,
  sendPatientDataReminder,
  sendCompanyConfirmationRequest,
  sendNoShowNotification,
  sendDoctorAbsenceNotification,
  isSendingPatientReminder,
  isSendingCompanyReminder,
  isSendingCompanyConfirmation,
  isSendingPatientDataReminder: isSendingDataReminder,
  isSendingCompanyConfirmationRequest,
  isSendingNoShowNotification: isSendingNoShow,
  isSendingDoctorAbsenceNotification: isSendingDoctorAbsence,
  uploadDailyDocuments,
  isUploadingDailyDocuments,
}: DailyAgendaViewProps) {
  const { t } = useTranslation()
  const { showNotification } = useNotification()

  const [isDailyDocUploadOpen, setIsDailyDocUploadOpen] = useState(false)

  // ============================================
  // STATE DEFINITIONS
  // ============================================

  const [movingAppointment, setMovingAppointment] = useState<{
    appointment: Appointment
    sourceDoctorId?: string
  } | null>(null)

  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    appointment: Appointment
    doctorId?: string
  } | null>(null)

  const [companyReminderDialog, setCompanyReminderDialog] = useState<{
    open: boolean
    appointmentId?: string
    companyName?: string
    additionalCount: number
  }>({
    open: false,
    appointmentId: undefined,
    companyName: undefined,
    additionalCount: 0,
  })

  const [companyConfirmationDialog, setCompanyConfirmationDialog] = useState<{
    open: boolean
    appointmentId?: string
    companyName?: string
    additionalCount: number
  }>({
    open: false,
    appointmentId: undefined,
    companyName: undefined,
    additionalCount: 0,
  })

  const [
    companyConfirmationRequestDialog,
    setCompanyConfirmationRequestDialog,
  ] = useState<{
    open: boolean
    appointmentId?: string
    companyName?: string
    additionalCount: number
  }>({
    open: false,
    appointmentId: undefined,
    companyName: undefined,
    additionalCount: 0,
  })

  const [doctorContextMenu, setDoctorContextMenu] = useState<{
    x: number
    y: number
    doctorId: string
    doctorName: string
  } | null>(null)

  const [isMoving, setIsMoving] = useState(false)
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const doctorContextMenuRef = useRef<HTMLDivElement>(null)

  const getErrorMessage = useCallback(
    (error: unknown) =>
      error instanceof Error ? error.message : t('common.error'),
    [t],
  )

  const isSendingAnyReminder =
    isSendingPatientReminder ||
    isSendingCompanyReminder ||
    isSendingDataReminder ||
    isSendingCompanyConfirmation ||
    isSendingCompanyConfirmationRequest

  const handleNoShowNotification = useCallback(async () => {
    const appointmentId = contextMenu?.appointment.id
    if (!appointmentId) return
    try {
      const res = await sendNoShowNotification(appointmentId)
      if (!res) return
      showNotification({
        message: res.message || t('common.success'),
        type: 'success',
      })
    } catch (error: unknown) {
      showNotification({
        message: getErrorMessage(error),
        type: 'error',
      })
    } finally {
      setContextMenu(null)
    }
  }, [
    contextMenu?.appointment.id,
    getErrorMessage,
    sendNoShowNotification,
    showNotification,
    t,
  ])

  const handleSendReminder = useCallback(
    async (type: 'patient' | 'company' | 'data') => {
      const appointmentId = contextMenu?.appointment.id
      if (!appointmentId) return
      try {
        let res: { success: boolean; message: string } | undefined
        if (type === 'patient') res = await sendPatientReminder(appointmentId)
        if (type === 'company')
          res = await sendCompanyReminder(appointmentId, 'single')
        if (type === 'data') res = await sendPatientDataReminder(appointmentId)
        if (!res) return

        showNotification({
          message: res.message || t('common.success'),
          type: 'success',
        })
      } catch (error: unknown) {
        showNotification({
          message: getErrorMessage(error),
          type: 'error',
        })
      } finally {
        setContextMenu(null)
      }
    },
    [
      getErrorMessage,
      contextMenu?.appointment.id,
      sendPatientReminder,
      sendCompanyReminder,
      sendPatientDataReminder,
      showNotification,
      t,
    ],
  )
  const handleSendConfirmationEmail = useCallback(async () => {
    const selectedAppointment = contextMenu?.appointment
    if (!selectedAppointment?.id || !selectedAppointment.companyId) {
      return
    }

    const additionalAppointmentsCount = appointments.filter(
      (apt) =>
        apt.companyId === selectedAppointment.companyId &&
        apt.id !== selectedAppointment.id,
    ).length

    if (additionalAppointmentsCount > 0) {
      setCompanyConfirmationDialog({
        open: true,
        appointmentId: selectedAppointment.id,
        companyName: selectedAppointment.companyName,
        additionalCount: additionalAppointmentsCount,
      })
      setContextMenu(null)
      return
    }

    try {
      const res = await sendCompanyConfirmation(
        selectedAppointment.id,
        'single',
      )
      if (!res) return
      showNotification({
        message: res.message || t('common.success'),
        type: 'success',
      })
    } catch (error: unknown) {
      showNotification({
        message: getErrorMessage(error),
        type: 'error',
      })
    } finally {
      setContextMenu(null)
    }
  }, [
    appointments,
    contextMenu?.appointment,
    getErrorMessage,
    sendCompanyConfirmation,
    showNotification,
    t,
  ])

  const handleCompanyReminderClick = useCallback(() => {
    const selectedAppointment = contextMenu?.appointment
    if (!selectedAppointment?.id || !selectedAppointment.companyId) {
      return
    }

    const additionalAppointmentsCount = appointments.filter(
      (apt) =>
        apt.companyId === selectedAppointment.companyId &&
        apt.id !== selectedAppointment.id,
    ).length

    if (additionalAppointmentsCount > 0) {
      setCompanyReminderDialog({
        open: true,
        appointmentId: selectedAppointment.id,
        companyName: selectedAppointment.companyName,
        additionalCount: additionalAppointmentsCount,
      })
      setContextMenu(null)
      return
    }

    handleSendReminder('company')
  }, [appointments, contextMenu?.appointment, handleSendReminder])

  const handleCompanyReminderScopeChoice = useCallback(
    async (scope: 'single' | 'all_company_for_date') => {
      const appointmentId = companyReminderDialog.appointmentId
      if (!appointmentId) return
      try {
        const res = await sendCompanyReminder(appointmentId, scope)
        if (!res) return
        showNotification({
          message: res.message || t('common.success'),
          type: 'success',
        })
      } catch (error: unknown) {
        showNotification({
          message: getErrorMessage(error),
          type: 'error',
        })
      } finally {
        setCompanyReminderDialog({
          open: false,
          appointmentId: undefined,
          companyName: undefined,
          additionalCount: 0,
        })
      }
    },
    [
      companyReminderDialog.appointmentId,
      getErrorMessage,
      sendCompanyReminder,
      showNotification,
      t,
    ],
  )

  const handleCompanyConfirmationScopeChoice = useCallback(
    async (scope: 'single' | 'all_company_for_date') => {
      const appointmentId = companyConfirmationDialog.appointmentId
      if (!appointmentId) return
      try {
        const res = await sendCompanyConfirmation(appointmentId, scope)
        if (!res) return
        showNotification({
          message: res.message || t('common.success'),
          type: 'success',
        })
      } catch (error: unknown) {
        showNotification({
          message: getErrorMessage(error),
          type: 'error',
        })
      } finally {
        setCompanyConfirmationDialog({
          open: false,
          appointmentId: undefined,
          companyName: undefined,
          additionalCount: 0,
        })
      }
    },
    [
      companyConfirmationDialog.appointmentId,
      getErrorMessage,
      sendCompanyConfirmation,
      showNotification,
      t,
    ],
  )

  const handleCompanyConfirmationRequestClick = useCallback(() => {
    const selectedAppointment = contextMenu?.appointment
    if (!selectedAppointment?.id || !selectedAppointment.companyId) {
      return
    }

    const additionalAppointmentsCount = appointments.filter(
      (apt) =>
        apt.companyId === selectedAppointment.companyId &&
        apt.id !== selectedAppointment.id,
    ).length

    if (additionalAppointmentsCount > 0) {
      setCompanyConfirmationRequestDialog({
        open: true,
        appointmentId: selectedAppointment.id,
        companyName: selectedAppointment.companyName,
        additionalCount: additionalAppointmentsCount,
      })
      setContextMenu(null)
      return
    }

    sendCompanyConfirmationRequest(selectedAppointment.id, 'single')
      .then((res) => {
        if (!res) return
        showNotification({
          message: res.message || t('common.success'),
          type: 'success',
        })
      })
      .catch((error: unknown) => {
        showNotification({
          message: getErrorMessage(error),
          type: 'error',
        })
      })
      .finally(() => {
        setContextMenu(null)
      })
  }, [
    appointments,
    contextMenu?.appointment,
    getErrorMessage,
    sendCompanyConfirmationRequest,
    showNotification,
    t,
  ])

  const handleCompanyConfirmationRequestScopeChoice = useCallback(
    async (scope: 'single' | 'all_company_for_date') => {
      const appointmentId = companyConfirmationRequestDialog.appointmentId
      if (!appointmentId) return
      try {
        const res = await sendCompanyConfirmationRequest(appointmentId, scope)
        if (!res) return
        showNotification({
          message: res.message || t('common.success'),
          type: 'success',
        })
      } catch (error: unknown) {
        showNotification({
          message: getErrorMessage(error),
          type: 'error',
        })
      } finally {
        setCompanyConfirmationRequestDialog({
          open: false,
          appointmentId: undefined,
          companyName: undefined,
          additionalCount: 0,
        })
      }
    },
    [
      companyConfirmationRequestDialog.appointmentId,
      getErrorMessage,
      sendCompanyConfirmationRequest,
      showNotification,
      t,
    ],
  )

  // Handler to mark all appointments as "effettuata" (completed)
  const handleMarkAllAsCompleted = useCallback(async () => {
    // Count appointments that will be updated
    const appointmentsToUpdate = appointments.filter(
      (apt) =>
        apt.status !== 'effettuata' &&
        apt.status !== 'non_presentato' &&
        apt.id &&
        !apt.analysisOnly, // Exclude analysis-only appointments
    )

    if (appointmentsToUpdate.length === 0) {
      return
    }

    if (
      !confirm(
        t('booking.confirmMarkAllCompleted', {
          count: appointmentsToUpdate.length,
          defaultValue: `Confermi di voler marcare ${appointmentsToUpdate.length} prenotazioni come "Effettuate"?`,
        }),
      )
    ) {
      return
    }

    try {
      await markAllAppointmentsCompleted(date)
    } catch (error) {
      console.error('Failed to update appointments:', error)
      alert(t('common.error', { defaultValue: 'Si è verificato un errore' }))
    }
  }, [appointments, date, markAllAppointmentsCompleted, t])

  // Build a map of time -> appointments for efficient lookup
  const appointmentsByTime = useMemo(() => {
    const map = new Map<string, Array<Appointment>>()
    appointments.forEach((apt) => {
      const existing = map.get(apt.time) || []
      map.set(apt.time, [...existing, apt])
    })
    return map
  }, [appointments])

  const availableDoctorSlots = useMemo(() => {
    if (!multiDoctorSlots) return []

    // Filter to show only doctors who are working today (have availability or bookings)
    // If we have a single selected doctor (filtered by parent), we might explicitly want to show them even if empty?
    // But usually 'multiDoctorSlots' passed here is already filtered by selection if active.
    // If the user selected a doctor, displayedSlots has 1 item. We should probably show it regardless?
    // But if they selected "All Doctors", we want to filter empty ones.

    // Helper to check if doctor has any activity today
    const hasActivity = (doctor: DoctorSlotsResponse) => {
      if (doctor.hasAvailability) return true
      // Check for bookings despite no availability (e.g. fully booked)
      return doctor.slots.some((s) => s.state === 'booked')
    }

    const activeDoctors = multiDoctorSlots.filter(hasActivity)

    // Sort doctors: prioritize those with bookings, and put those with region slots at the end
    return activeDoctors.sort((a, b) => {
      const aHasBookings = a.slots.some((s) => s.state === 'booked')
      const bHasBookings = b.slots.some((s) => s.state === 'booked')

      if (aHasBookings && !bHasBookings) return -1
      if (!aHasBookings && bHasBookings) return 1

      const aHasRegion = a.slots.some((s) => s.region)
      const bHasRegion = b.slots.some((s) => s.region)

      if (aHasRegion && !bHasRegion) return 1
      if (!aHasRegion && bHasRegion) return -1

      return 0
    })
  }, [multiDoctorSlots])

  // Group every special type independently so overlapping notices remain visible.
  const doctorSpecialGroups = useMemo(() => {
    const result = new Map<string, Array<SlotGroup & { id: string }>>()
    availableDoctorSlots.forEach((doctor) => {
      result.set(
        doctor.doctorId,
        groupSpecialSlots(doctor.slots).map((group) => ({
          ...group,
          id: `${doctor.doctorId}-${group.startTime}-${group.type}`,
        })),
      )
    })
    return result
  }, [availableDoctorSlots])

  // Check if any doctor has special groups to show
  const hasAnySpecialGroups = useMemo(() => {
    let has = false
    doctorSpecialGroups.forEach((groups) => {
      if (groups.length > 0) has = true
    })
    return has
  }, [doctorSpecialGroups])

  // Filter analysis-only appointments for dedicated column
  const analysisOnlyAppointments = useMemo(() => {
    return appointments.filter((apt) => apt.analysisOnly === true)
  }, [appointments])

  // Check if we should show analysis column
  const hasAnalysisOnlyAppointments = analysisOnlyAppointments.length > 0

  const handleAddBooking = useCallback(
    (time: string, doctorId?: string, extraCompanyId?: string) => {
      onAddBooking(
        date,
        time,
        doctorId || doctorSlots?.doctorId,
        extraCompanyId,
      )
    },
    [date, doctorSlots?.doctorId, onAddBooking],
  )

  // ============================================
  // MOVE APPOINTMENT ACTIONS
  // ============================================

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(e.target as Node)
      ) {
        setContextMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [contextMenu])

  useEffect(() => {
    if (!doctorContextMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        doctorContextMenuRef.current &&
        !doctorContextMenuRef.current.contains(e.target as Node)
      ) {
        setDoctorContextMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [doctorContextMenu])

  // Cancel move on Escape
  useEffect(() => {
    if (!movingAppointment) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMovingAppointment(null)
        setContextMenu(null)
        setDoctorContextMenu(null)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [movingAppointment])

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, appointment: Appointment, doctorId?: string) => {
      if (userRole !== 'admin') return
      e.preventDefault()
      // Adjust position to stay within viewport
      const x = Math.min(e.clientX, window.innerWidth - 200)
      const y = Math.min(e.clientY, window.innerHeight - 150)
      setContextMenu({ x, y, appointment, doctorId })
    },
    [userRole],
  )

  const handleDoctorCellClick = useCallback(
    (e: React.MouseEvent, doctorId: string, doctorName: string) => {
      if (userRole !== 'admin') return
      e.preventDefault()
      const x = Math.min(e.clientX, window.innerWidth - 200)
      const y = Math.min(e.clientY, window.innerHeight - 100)
      setContextMenu(null)
      setDoctorContextMenu({ x, y, doctorId, doctorName })
    },
    [userRole],
  )

  const handleDoctorAbsenceNotification = useCallback(async () => {
    if (!doctorContextMenu) return
    try {
      const res = await sendDoctorAbsenceNotification({
        doctorId: doctorContextMenu.doctorId,
        date,
      })
      if (!res) return
      showNotification({
        message: res.message || t('common.success'),
        type: 'success',
      })
    } catch (error: unknown) {
      showNotification({
        message: getErrorMessage(error),
        type: 'error',
      })
    } finally {
      setDoctorContextMenu(null)
    }
  }, [
    date,
    doctorContextMenu,
    getErrorMessage,
    sendDoctorAbsenceNotification,
    showNotification,
    t,
  ])

  const handleSingleAppointmentAbsenceNotification = useCallback(async () => {
    if (!contextMenu) return
    const doctorId = contextMenu.appointment.doctorId ?? contextMenu.doctorId
    if (!doctorId) return
    try {
      const res = await sendDoctorAbsenceNotification({
        doctorId,
        date,
        appointmentId: contextMenu.appointment.id,
      })
      if (!res) return
      showNotification({
        message: res.message || t('common.success'),
        type: 'success',
      })
    } catch (error: unknown) {
      showNotification({
        message: getErrorMessage(error),
        type: 'error',
      })
    } finally {
      setContextMenu(null)
    }
  }, [
    contextMenu,
    date,
    getErrorMessage,
    sendDoctorAbsenceNotification,
    showNotification,
    t,
  ])

  const handleStartMove = useCallback(() => {
    if (!contextMenu) return
    setMovingAppointment({
      appointment: contextMenu.appointment,
      sourceDoctorId: contextMenu.doctorId,
    })
    setContextMenu(null)
  }, [contextMenu])

  const handleCancelMove = useCallback(() => {
    setMovingAppointment(null)
  }, [])

  const handleMoveToSlot = useCallback(
    async (time: string, doctorId?: string) => {
      if (!movingAppointment?.appointment.id || !onMoveAppointment) return
      setIsMoving(true)
      try {
        await onMoveAppointment(
          movingAppointment.appointment,
          date,
          time,
          doctorId || doctorSlots?.doctorId,
        )
        setMovingAppointment(null)
      } catch {
        // Keep moving state so user can retry
      } finally {
        setIsMoving(false)
      }
    },
    [movingAppointment, onMoveAppointment, date, doctorSlots?.doctorId],
  )

  const appointmentCardActions = useMemo<AppointmentCardActions>(
    () => ({
      companyIdsWithUsers,
      updateStatus: updateAppointmentStatus,
      updateAcceptance: updateAppointmentAcceptance,
      removeIdoneity: removeAppointmentIdoneity,
      isRemovingIdoneity: isRemovingAppointmentIdoneity,
      uploadIdoneity,
      isUploadingIdoneity,
    }),
    [
      companyIdsWithUsers,
      isRemovingAppointmentIdoneity,
      isUploadingIdoneity,
      removeAppointmentIdoneity,
      updateAppointmentAcceptance,
      updateAppointmentStatus,
      uploadIdoneity,
    ],
  )

  return (
    <AppointmentCardActionsContext.Provider value={appointmentCardActions}>
      <div className="flex flex-col h-full bg-white overflow-hidden">
        {/* 1. Date Navigation Header - Static Top */}
        <div className="bg-white border-b shadow-xs shrink-0 relative">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 md:gap-4 flex-1">
              <Button
                variant="outline"
                size="sm"
                onClick={onPreviousDay}
                className="h-8 w-8 p-0 shrink-0"
              >
                <IconRenderer name="chevron-left" className="w-4 h-4" />
              </Button>
              <h2 className="text-base md:text-lg font-semibold capitalize text-center truncate flex-1 md:flex-none md:min-w-[200px]">
                {formatDate(date)}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={onNextDay}
                className="h-8 w-8 p-0 shrink-0"
              >
                <IconRenderer name="chevron-right" className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex items-center gap-3">
              {/* Mark All as Completed Button */}
              {(userRole === 'doctor' || userRole === 'admin') &&
                appointments.filter(
                  (apt) =>
                    apt.status !== 'effettuata' &&
                    apt.status !== 'non_presentato' &&
                    !apt.analysisOnly,
                ).length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllAsCompleted}
                    disabled={isMarkingAll}
                    className="h-8 px-3 text-green-600 border-green-200 hover:bg-green-50 disabled:opacity-50"
                    title={t('booking.markAllAsCompleted', {
                      defaultValue: 'Marca tutte come effettuate',
                    })}
                  >
                    <IconRenderer
                      name={isMarkingAll ? 'loader-2' : 'check-circle'}
                      className={cn(
                        'w-4 h-4 mr-2',
                        isMarkingAll && 'animate-spin',
                      )}
                    />
                    <span className="hidden md:inline">
                      {t('booking.markAllCompleted', {
                        defaultValue: 'Tutte effettuate',
                      })}
                    </span>
                  </Button>
                )}

              <div className="hidden md:flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  <span>{t('agenda.available')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span>{t('agenda.booked')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-50" />
                  <span>{t('agenda.blocked')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Note Banner - visible when note exists */}
          {note?.content && (
            <div
              className={cn(
                'px-4 py-2.5 bg-amber-50 border-b border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors',
                'flex items-center gap-3',
              )}
              onClick={onNoteClick}
            >
              <IconRenderer
                name="sticky-note"
                className="w-5 h-5 text-amber-600 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-amber-900 font-medium truncate">
                  {note.content}
                </p>
              </div>
              <IconRenderer
                name="edit-2"
                className="w-4 h-4 text-amber-400 shrink-0"
              />
            </div>
          )}

          {/* Daily Documents Row (Admin Only) */}
          {userRole === 'admin' && (
            <div className="px-4 py-2 border-b bg-slate-50 flex items-center gap-3 overflow-x-auto">
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                {t('documents.dailyDocuments', {
                  defaultValue: 'Documenti Giornata',
                })}
                :
              </span>
              {dailyDocuments.map((doc) => (
                <a
                  key={doc.id}
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs hover:border-blue-300 hover:text-blue-600 transition-colors whitespace-nowrap shadow-xs"
                >
                  <IconRenderer name="file-text" className="w-3.5 h-3.5" />
                  <span
                    className="truncate max-w-[150px] font-medium"
                    title={doc.filename}
                  >
                    {doc.filename}
                  </span>
                </a>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs rounded-full border-dashed shrink-0"
                onClick={() => setIsDailyDocUploadOpen(true)}
              >
                <IconRenderer name="upload" className="w-3.5 h-3.5 mr-1" />
                {t('common.upload', { defaultValue: 'Carica' })}
              </Button>
            </div>
          )}

          {/* Move Appointment Banner */}
          {movingAppointment && (
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center gap-3">
              <IconRenderer
                name="move"
                className="w-5 h-5 text-blue-600 shrink-0"
              />
              <span className="flex-1 text-sm font-medium text-blue-900">
                {isMoving
                  ? t('booking.movingAppointment', {
                      defaultValue: 'Spostamento in corso...',
                    })
                  : t('booking.clickSlotToMove', {
                      patient: movingAppointment.appointment.patientName,
                      defaultValue: `Clicca su uno slot per spostare l'appuntamento di ${movingAppointment.appointment.patientName}`,
                    })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelMove}
                disabled={isMoving}
                className="h-7 text-xs"
              >
                {t('app.cancel', { defaultValue: 'Annulla' })}
              </Button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto relative">
          {/* Common wrapper to ensure consistent width across all sections */}
          <div
            className={`${availableDoctorSlots.length > 1 ? 'min-w-max' : 'min-w-full'}`}
          >
            {/* 1.5 Analysis-Only Row - Single Slot at the top */}
            {viewMode === 'multi' && hasAnalysisOnlyAppointments && (
              <div className="flex border-b bg-purple-50/30 min-h-[80px]">
                <div
                  className={cn(
                    'w-16 shrink-0 border-r flex flex-col items-center justify-center p-2 text-xs font-medium text-purple-700 text-center relative group cursor-pointer',
                    'bg-purple-50 sticky left-0 z-20 shadow-[1px_0_3px_rgba(0,0,0,0.05)]',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddAnalysisOnlyBooking?.(date, ANALYSIS_START_TIME)
                  }}
                >
                  <span className="group-hover:opacity-0 transition-opacity">
                    {t('booking.analysisColumn')}
                  </span>
                  {userRole === 'admin' && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <IconRenderer name="plus" className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 p-3 flex gap-3 overflow-x-auto items-start">
                  {analysisOnlyAppointments
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((apt) => (
                      <div
                        key={apt.id}
                        className="min-w-[220px] max-w-[50vw] shrink-0 flex flex-col gap-1.5"
                      >
                        <span className="text-[11px] font-bold text-purple-700 bg-purple-100/60 px-2 py-0.5 rounded-sm w-fit border border-purple-200">
                          {apt.time}
                        </span>
                        <AppointmentCard
                          appointment={apt}
                          userRole={userRole}
                          onClick={() => onAppointmentClick(apt)}
                          onDelete={onDelete}
                          onDuplicate={onDuplicate}
                          onContextMenu={(e, a) => handleContextMenu(e, a)}
                          isBeingMoved={false}
                          isMoveModeActive={false}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* 2. Doctor Columns Header - Sticky Top of Scroll Area */}
            {viewMode === 'multi' && availableDoctorSlots.length > 0 && (
              <div className="flex border-b bg-gray-50 sticky top-0 z-40 shadow-sm">
                {/* Time Column Placeholder in Header */}
                <div className="w-16 shrink-0 border-r bg-gray-50 sticky left-0 z-50" />

                {availableDoctorSlots.map((doctor) => {
                  const isAvailable = doctorsAvailability[doctor.doctorId]
                  return (
                    <div
                      key={doctor.doctorId}
                      className={cn(
                        'flex-1 min-w-[30vw] px-3 py-3 text-center border-r font-medium text-sm text-gray-700 flex flex-col items-center gap-1',
                        userRole === 'admin' &&
                          'cursor-pointer hover:bg-gray-100 transition-colors',
                      )}
                      style={{
                        borderTop: `3px solid ${doctor.doctorColor || '#6b7280'}`,
                      }}
                      onClick={(e) =>
                        handleDoctorCellClick(
                          e,
                          doctor.doctorId,
                          doctor.doctorName,
                        )
                      }
                    >
                      <div className="flex items-center gap-2 justify-center w-full">
                        <span
                          className="truncate block"
                          title={doctor.doctorName}
                        >
                          {doctor.doctorName}
                        </span>
                        {userRole === 'admin' && (
                          <div
                            className={cn(
                              'w-2 h-2 rounded-full shrink-0',
                              isAvailable ? 'bg-green-500' : 'bg-red-500',
                            )}
                            title={
                              isAvailable
                                ? t('availability.available')
                                : t('availability.unavailable')
                            }
                          />
                        )}
                      </div>
                      <span className="text-[10px] font-normal text-gray-500">
                        {doctor.slotDurationMinutes} min
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Special Groups Row - Shows company visits, site inspections, extra locations, regions */}
            {viewMode === 'multi' && hasAnySpecialGroups && (
              <div className="flex border-b bg-gray-50/50">
                {/* Time Column Placeholder */}
                <div className="w-16 shrink-0 border-r bg-gray-50 sticky left-0 z-40" />

                {/* Doctor Special Groups */}
                {availableDoctorSlots.map((doctor) => {
                  const groups = doctorSpecialGroups.get(doctor.doctorId) || []
                  return (
                    <div
                      key={doctor.doctorId}
                      className="flex-1 min-w-[30vw] p-2 border-r flex flex-wrap gap-2"
                    >
                      {groups.map((group) => {
                        if (group.type === 'companyVisit') {
                          const data =
                            group.data as CalculatedSlot['companyVisit']
                          return (
                            <div
                              key={group.id}
                              className="flex flex-col gap-0.5 bg-purple-50 border border-purple-200 rounded p-2 flex-1 min-w-[150px]"
                            >
                              <div className="flex items-center gap-1">
                                <IconRenderer
                                  name="building"
                                  className="w-3.5 h-3.5 text-purple-600 shrink-0"
                                />
                                <span className="text-xs font-semibold text-purple-700 truncate">
                                  {data?.companyName}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <IconRenderer
                                  name="clock"
                                  className="w-3 h-3 text-purple-500 shrink-0"
                                />
                                <span className="text-[10px] font-medium text-purple-600">
                                  {group.startTime} - {group.endTime}
                                </span>
                              </div>
                              {data?.companyAddress && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                  <IconRenderer
                                    name="map-pin"
                                    className="w-2.5 h-2.5 text-purple-500 shrink-0"
                                  />
                                  <span className="truncate">
                                    {data.companyAddress}
                                  </span>
                                </div>
                              )}
                              {data?.patientNames &&
                                data.patientNames.length > 0 && (
                                  <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                    <IconRenderer
                                      name="users"
                                      className="w-2.5 h-2.5 text-purple-500 shrink-0"
                                    />
                                    <span className="truncate">
                                      {data.patientNames.length === 1
                                        ? data.patientNames[0]
                                        : `${data.patientNames.length} dipendenti`}
                                    </span>
                                  </div>
                                )}
                              {data?.notes && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                  <IconRenderer
                                    name="message-square"
                                    className="w-2.5 h-2.5 text-purple-500 shrink-0"
                                  />
                                  <span>{data.notes}</span>
                                </div>
                              )}
                            </div>
                          )
                        }

                        if (group.type === 'siteInspection') {
                          const data =
                            group.data as CalculatedSlot['siteInspection']
                          return (
                            <div
                              key={group.id}
                              className="flex flex-col gap-0.5 bg-orange-50 border border-orange-200 rounded p-2 flex-1 min-w-[150px]"
                            >
                              <div className="flex items-center gap-1">
                                <IconRenderer
                                  name="clipboard-check"
                                  className="w-3.5 h-3.5 text-orange-600 shrink-0"
                                />
                                <span className="text-xs font-semibold text-orange-700 truncate">
                                  {data?.companyName ||
                                    t('booking.siteInspection')}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <IconRenderer
                                  name="clock"
                                  className="w-3 h-3 text-orange-500 shrink-0"
                                />
                                <span className="text-[10px] font-medium text-orange-600">
                                  {group.startTime} - {group.endTime}
                                </span>
                              </div>
                              {data?.address && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                  <IconRenderer
                                    name="map-pin"
                                    className="w-2.5 h-2.5 text-orange-500 shrink-0"
                                  />
                                  <span className="truncate">
                                    {data.address}
                                  </span>
                                </div>
                              )}
                              {data?.phone && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                  <IconRenderer
                                    name="phone"
                                    className="w-2.5 h-2.5 text-orange-500 shrink-0"
                                  />
                                  <span className="truncate">{data.phone}</span>
                                </div>
                              )}
                              {data?.notes && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                  <IconRenderer
                                    name="message-square"
                                    className="w-2.5 h-2.5 text-orange-500 shrink-0"
                                  />
                                  <span>{data.notes}</span>
                                </div>
                              )}
                            </div>
                          )
                        }

                        if (group.type === 'extra') {
                          const data = group.data as ExtraData
                          return (
                            <div
                              key={group.id}
                              className="flex flex-col gap-0.5 bg-green-50 border border-green-200 rounded p-2 flex-1 min-w-[150px]"
                            >
                              <div className="flex items-center gap-1">
                                <IconRenderer
                                  name={
                                    data.extraCompanyName
                                      ? 'building'
                                      : 'map-pin'
                                  }
                                  className="w-3.5 h-3.5 text-green-600 shrink-0"
                                />
                                <span className="text-xs font-semibold text-green-700 truncate">
                                  {data.extraCompanyName || data.extraLocation}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <IconRenderer
                                  name="clock"
                                  className="w-3 h-3 text-green-500 shrink-0"
                                />
                                <span className="text-[10px] font-medium text-green-600">
                                  {group.startTime} - {group.endTime}
                                </span>
                              </div>
                              {data.extraCompanyName &&
                                (data.extraCompanyAddress ||
                                  data.extraLocation) && (
                                  <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                    <IconRenderer
                                      name="map-pin"
                                      className="w-2.5 h-2.5 text-green-500 shrink-0"
                                    />
                                    <span className="truncate">
                                      {data.extraCompanyAddress ||
                                        data.extraLocation}
                                    </span>
                                  </div>
                                )}
                              {data.extraNotes && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-600">
                                  <IconRenderer
                                    name="message-square"
                                    className="w-2.5 h-2.5 text-green-500 shrink-0"
                                  />
                                  <span>{data.extraNotes}</span>
                                </div>
                              )}
                            </div>
                          )
                        }

                        // region type (group.type === 'region')
                        return (
                          <div
                            key={group.id}
                            className="flex flex-col gap-0.5 bg-red-50 border border-red-200 rounded p-2 flex-1 min-w-[150px]"
                          >
                            <div className="flex items-center gap-1">
                              <IconRenderer
                                name="map"
                                className="w-3.5 h-3.5 text-red-600 shrink-0"
                              />
                              <span className="text-xs font-semibold text-red-700 truncate">
                                {(group.data && 'reason' in group.data
                                  ? group.data.reason
                                  : null) || 'Regione'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <IconRenderer
                                name="clock"
                                className="w-3 h-3 text-red-500 shrink-0"
                              />
                              <span className="text-[10px] font-medium text-red-600">
                                {group.startTime} - {group.endTime}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )}

            {/* 3. Main Grid Content */}
            <div className="pb-10">
              {viewMode === 'single' ? (
                (doctorSlots?.slots ?? []).map((slot) => (
                  <div key={slot.time} className="relative">
                    <SlotRow
                      time={slot.time}
                      displayTime={`${slot.time}–${slot.endTime}`}
                      slot={slot}
                      appointments={appointmentsByTime.get(slot.time) || []}
                      userRole={userRole}
                      onAddBooking={() =>
                        handleAddBooking(
                          slot.time,
                          undefined,
                          slot.extraCompanyId,
                        )
                      }
                      onAppointmentClick={(appointment, doctorId) => {
                        onAppointmentClick(appointment, doctorId)
                      }}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      onContextMenu={(e, a) => handleContextMenu(e, a)}
                      movingAppointmentId={movingAppointment?.appointment.id}
                      onSlotClickForMove={(slotTime) =>
                        handleMoveToSlot(slotTime)
                      }
                    />
                  </div>
                ))
              ) : availableDoctorSlots.length > 0 ? (
                <MultiDoctorAgenda
                  doctors={availableDoctorSlots}
                  appointments={appointments}
                  userRole={userRole}
                  movingAppointmentId={movingAppointment?.appointment.id}
                  onAddBooking={(time, doctorId, extraCompanyId) =>
                    handleAddBooking(time, doctorId, extraCompanyId)
                  }
                  onMoveToSlot={(time, doctorId) =>
                    handleMoveToSlot(time, doctorId)
                  }
                  onAppointmentClick={(appointment, doctorId) =>
                    onAppointmentClick(appointment, doctorId)
                  }
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onContextMenu={(event, appointment, doctorId) =>
                    handleContextMenu(event, appointment, doctorId)
                  }
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
                  <IconRenderer
                    name="users"
                    className="w-12 h-12 mb-4 text-gray-300"
                  />
                  <p className="text-lg">{t('agenda.noDoctorsAvailable')}</p>
                  <p className="text-sm text-gray-400">
                    {t('agenda.adjustFilters')}
                  </p>
                </div>
              )}
            </div>
          </div>
          {/* Close min-w-max wrapper */}
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <div
            ref={contextMenuRef}
            className="fixed z-9999 min-w-[160px] rounded-md border border-gray-200 bg-white p-1 shadow-md"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button
              type="button"
              className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-green-50 text-green-700 gap-2"
              onClick={() => {
                handleAddBooking(
                  contextMenu.appointment.time,
                  contextMenu.doctorId,
                )
                setContextMenu(null)
              }}
            >
              <IconRenderer name="plus" className="w-4 h-4" />
              {t('booking.addBooking', { defaultValue: 'Aggiungi' })}
            </button>
            {!contextMenu.appointment.analysisOnly && (
              <button
                type="button"
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 gap-2"
                onClick={() => {
                  handleStartMove()
                }}
              >
                <IconRenderer name="move" className="w-4 h-4" />
                {t('booking.move', { defaultValue: 'Sposta' })}
              </button>
            )}
            {contextMenu.appointment.patientId && (
              <button
                type="button"
                disabled={isSendingAnyReminder}
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 disabled:opacity-50 gap-2"
                onClick={() => handleSendReminder('patient')}
              >
                <IconRenderer name="user" className="w-4 h-4" />
                {t('booking.sendPatientReminder', {
                  defaultValue: 'Promemoria Paziente',
                })}
              </button>
            )}
            {contextMenu.appointment.companyId && (
              <button
                type="button"
                disabled={isSendingAnyReminder}
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 disabled:opacity-50 gap-2"
                onClick={handleCompanyReminderClick}
              >
                <IconRenderer name="building" className="w-4 h-4" />
                {t('booking.sendCompanyReminder', {
                  defaultValue: 'Promemoria Azienda',
                })}
              </button>
            )}
            {
              <button
                type="button"
                disabled={isSendingAnyReminder}
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-amber-50 text-amber-700 gap-2 disabled:opacity-50"
                onClick={() => handleSendReminder('data')}
              >
                <IconRenderer name="mail" className="w-4 h-4 text-amber-500" />
                {t('booking.requestMissingData', {
                  defaultValue: 'Richiedi dati paziente',
                })}
              </button>
            }
            {
              <button
                type="button"
                disabled={isSendingAnyReminder}
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 disabled:opacity-50 gap-2"
                onClick={() => handleSendConfirmationEmail()}
              >
                <IconRenderer name="mail" className="w-4 h-4" />
                {t('booking.sendCompanyConfirmationEmail', {
                  defaultValue: "Invia email di conferma all'azienda",
                })}
              </button>
            }
            {
              <button
                type="button"
                disabled={isSendingAnyReminder}
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 disabled:opacity-50 gap-2"
                onClick={handleCompanyConfirmationRequestClick}
              >
                <IconRenderer name="mail-question" className="w-4 h-4" />
                {t('booking.requestCompanyConfirmation', {
                  defaultValue: 'Richiesta conferma appuntamento',
                })}
              </button>
            }
            <div className="-mx-1 my-1 h-px bg-gray-200" />
            <button
              type="button"
              disabled={isSendingNoShow}
              className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-orange-50 text-orange-700 gap-2 disabled:opacity-50"
              onClick={() => handleNoShowNotification()}
            >
              <IconRenderer name="user-x" className="w-4 h-4" />
              {t('booking.noShowNotification', {
                defaultValue: 'Email avviso mancata presenza',
              })}
            </button>
            {(contextMenu.appointment.doctorId ?? contextMenu.doctorId) && (
              <button
                type="button"
                disabled={isSendingDoctorAbsence}
                className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-orange-50 text-orange-700 gap-2 disabled:opacity-50"
                onClick={() => handleSingleAppointmentAbsenceNotification()}
              >
                <IconRenderer name="user-x" className="w-4 h-4" />
                {t('booking.doctorAbsenceNotification', {
                  defaultValue: 'Mancata presenza del medico',
                })}
              </button>
            )}
            <div className="-mx-1 my-1 h-px bg-gray-200" />
            <button
              type="button"
              className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 gap-2"
              onClick={() => {
                onDuplicate(contextMenu.appointment)
                setContextMenu(null)
              }}
            >
              <IconRenderer name="copy" className="w-4 h-4" />
              {t('common.duplicate', { defaultValue: 'Duplica' })}
            </button>
            {userRole === 'admin' && (
              <>
                <div className="-mx-1 my-1 h-px bg-gray-200" />
                <button
                  type="button"
                  className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-red-50 text-red-600 gap-2"
                  onClick={() => {
                    if (contextMenu.appointment.id) {
                      onDelete(contextMenu.appointment.id)
                    }
                    setContextMenu(null)
                  }}
                >
                  <IconRenderer name="trash-2" className="w-4 h-4" />
                  {t('app.delete', { defaultValue: 'Elimina' })}
                </button>
              </>
            )}
          </div>
        )}

        {/* Doctor Context Menu */}
        {doctorContextMenu && (
          <div
            ref={doctorContextMenuRef}
            className="fixed z-[9999] min-w-[160px] rounded-md border border-gray-200 bg-white p-1 shadow-md"
            style={{ top: doctorContextMenu.y, left: doctorContextMenu.x }}
          >
            <button
              type="button"
              disabled={isSendingDoctorAbsence}
              className="flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-orange-50 text-orange-700 gap-2 disabled:opacity-50"
              onClick={() => handleDoctorAbsenceNotification()}
            >
              <IconRenderer name="user-x" className="w-4 h-4" />
              {t('booking.doctorAbsenceNotification', {
                defaultValue: 'Mancata presenza del medico',
              })}
            </button>
          </div>
        )}

        <Dialog
          open={companyReminderDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setCompanyReminderDialog({
                open: false,
                appointmentId: undefined,
                companyName: undefined,
                additionalCount: 0,
              })
            }
          }}
        >
          <DialogContent className="min-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <IconRenderer name="mail" className="w-5 h-5 text-blue-600" />
                {t('booking.sendCompanyReminder', {
                  defaultValue: 'Promemoria Azienda',
                })}
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed">
                {`Sono presenti altre ${companyReminderDialog.additionalCount} prenotazioni per ${companyReminderDialog.companyName || 'questa azienda'} nella stessa giornata.`}
                <br />
                {t('booking.chooseReminderType', {
                  defaultValue:
                    'Vuoi inviare una mail unica con tutti i pazienti oppure solo per la prenotazione selezionata?',
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
              {`Totale coinvolte: ${companyReminderDialog.additionalCount + 1} prenotazioni`}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleCompanyReminderScopeChoice('single')}
                disabled={isSendingCompanyReminder}
              >
                <IconRenderer name="user" className="w-4 h-4 mr-2" />
                {t('booking.reminderSingle', {
                  defaultValue: 'Solo questa prenotazione',
                })}
              </Button>
              <Button
                onClick={() =>
                  handleCompanyReminderScopeChoice('all_company_for_date')
                }
                disabled={isSendingCompanyReminder}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <IconRenderer name="users" className="w-4 h-4 mr-2" />
                {t('booking.reminderAllCompany', {
                  defaultValue: 'Mail unica per azienda',
                })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={companyConfirmationDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setCompanyConfirmationDialog({
                open: false,
                appointmentId: undefined,
                companyName: undefined,
                additionalCount: 0,
              })
            }
          }}
        >
          <DialogContent className="min-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <IconRenderer name="mail" className="w-5 h-5 text-blue-600" />
                {t('booking.sendCompanyConfirmationEmail', {
                  defaultValue: "Invia email di conferma all'azienda",
                })}
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed">
                {`Sono presenti altre ${companyConfirmationDialog.additionalCount} prenotazioni per ${companyConfirmationDialog.companyName || 'questa azienda'} nella stessa giornata.`}
                <br />
                {t('booking.chooseConfirmationType', {
                  defaultValue:
                    "Vuoi inviare la conferma solo per la prenotazione selezionata oppure per tutte le prenotazioni dell'azienda in questa giornata?",
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
              {`Totale coinvolte: ${companyConfirmationDialog.additionalCount + 1} prenotazioni`}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleCompanyConfirmationScopeChoice('single')}
                disabled={isSendingCompanyConfirmation}
              >
                <IconRenderer name="user" className="w-4 h-4 mr-2" />
                {t('booking.confirmationSingle', {
                  defaultValue: 'Solo questa prenotazione',
                })}
              </Button>
              <Button
                onClick={() =>
                  handleCompanyConfirmationScopeChoice('all_company_for_date')
                }
                disabled={isSendingCompanyConfirmation}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <IconRenderer name="users" className="w-4 h-4 mr-2" />
                {t('booking.confirmationAllCompany', {
                  defaultValue: "Tutte le prenotazioni dell'azienda",
                })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={companyConfirmationRequestDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setCompanyConfirmationRequestDialog({
                open: false,
                appointmentId: undefined,
                companyName: undefined,
                additionalCount: 0,
              })
            }
          }}
        >
          <DialogContent className="min-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <IconRenderer
                  name="mail-question"
                  className="w-5 h-5 text-blue-600"
                />
                {t('booking.requestCompanyConfirmation', {
                  defaultValue: 'Richiesta conferma appuntamento',
                })}
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed">
                {`Sono presenti altre ${companyConfirmationRequestDialog.additionalCount} prenotazioni per ${companyConfirmationRequestDialog.companyName || 'questa azienda'} nella stessa giornata.`}
                <br />
                {t('booking.chooseConfirmationRequestType', {
                  defaultValue:
                    "Vuoi inviare la richiesta di conferma solo per questa prenotazione oppure per tutte le prenotazioni dell'azienda in questa giornata?",
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
              {`Totale coinvolte: ${companyConfirmationRequestDialog.additionalCount + 1} prenotazioni`}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() =>
                  handleCompanyConfirmationRequestScopeChoice('single')
                }
                disabled={isSendingCompanyConfirmationRequest}
              >
                <IconRenderer name="user" className="w-4 h-4 mr-2" />
                {t('booking.confirmationSingle', {
                  defaultValue: 'Solo questa prenotazione',
                })}
              </Button>
              <Button
                onClick={() =>
                  handleCompanyConfirmationRequestScopeChoice(
                    'all_company_for_date',
                  )
                }
                disabled={isSendingCompanyConfirmationRequest}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <IconRenderer name="users" className="w-4 h-4 mr-2" />
                {t('booking.confirmationAllCompany', {
                  defaultValue: "Tutte le prenotazioni dell'azienda",
                })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Daily Document Upload Dialog */}
        <DailyDocumentUploadDialog
          open={isDailyDocUploadOpen}
          onOpenChange={setIsDailyDocUploadOpen}
          agendaDate={date}
          onSubmit={async (files) => {
            await uploadDailyDocuments(date, files)
          }}
          isPending={isUploadingDailyDocuments}
        />
      </div>
    </AppointmentCardActionsContext.Provider>
  )
})
